import { Dayjs } from 'dayjs'
import maxBy from 'lodash/maxBy'
import minBy from 'lodash/minBy'

import { add, DATE_TEMPLATES, day, format, getToday, isAfter, isBefore, subtract } from '~/astra'
// Type-only: `~/storm` imports this module for a value, so a runtime import would close a cycle.
import type { Location, LocationServiceOverride } from '~/storm'
import { closedReasonText } from '~/utils/locationServiceOverride'
import { ServicePeriod, workingWeekdays } from '~/utils/servicePeriods'

// Norwegian plural weekday form ("mandag" -> "mandager")
function toPluralWeekday(dayOfWeek: string) {
    return `${format(getToday().isoWeekday(Number(dayOfWeek)), DATE_TEMPLATES.fullWeekday)}er`
}

function formatClosedWeekdays(closedWeekdays: string[]) {
    // No need to list weekdays when the room is closed every working day (or, defensively, none match)
    if (closedWeekdays.length === 0 || closedWeekdays.length === workingWeekdays.length) return ''

    const names = closedWeekdays.map(toPluralWeekday)
    if (names.length === 1) return ` på ${names[0]}`
    return ` på ${names.slice(0, -1).join(', ')} og ${names[names.length - 1]}`
}

function formatDateRange(startDate: string | null | undefined, endDate: string | null | undefined) {
    const start = startDate ? format(day(startDate), DATE_TEMPLATES.longMonth) : undefined
    const end = endDate ? format(day(endDate), DATE_TEMPLATES.longMonth) : undefined

    if (start && end && startDate === endDate) return ` den ${start}`
    if (start && end) return ` fra ${start} til ${end}`
    if (start) return ` fra ${start} og fremover`
    if (end) return ` frem til ${end}`
    return ''
}

export type ClosedLocationText = {
    label: string
    description: string
}

export function getOverrideClosedLocationText(locationServiceOverride: LocationServiceOverride): ClosedLocationText {
    const label = closedReasonText[locationServiceOverride.closed_reason]

    return {
        label,
        description: `Stengt stue: ${label}`,
    }
}

export function getServicePeriodClosedLocationText(servicePeriod: ServicePeriod, closedWeekdays: string[]): ClosedLocationText {
    const weekdays = formatClosedWeekdays(closedWeekdays)
    const range = formatDateRange(servicePeriod.start_date, servicePeriod.end_date)

    return {
        label: closedReasonText.service_period,
        description: `Stengt via Sykehusoppsett${weekdays}${range}`,
    }
}

// A service period sets up a room's opening hours on some weekdays within a date range. It covers a day that falls in
// that range on one of those weekdays, whether it marks the room open or closed. A day that no service period covers
// has no opening hours set up, and its description names the nearest covered days around it.
//
// A period's nearest covered day before a date is found by starting from the day before, or from the period's end date
// when the period ended earlier, and checking at most a week back: the period's weekdays all recur within seven days.
// The nearest covered day after a date mirrors this.

const daysInWeek = 7

/** A service period's date range as days. Either end may be open. */
function dateRangeOf(servicePeriod: ServicePeriod) {
    return {
        start: servicePeriod.start_date ? day(servicePeriod.start_date) : undefined,
        end: servicePeriod.end_date ? day(servicePeriod.end_date) : undefined,
    }
}

/** Whether `servicePeriod` covers a date. */
function coveredBy(servicePeriod: ServicePeriod) {
    const { start, end } = dateRangeOf(servicePeriod)

    return (date: Dayjs) => {
        if (start && isBefore(date, start, 'day')) return false
        if (end && isAfter(date, end, 'day')) return false

        const weekday = date.isoWeekday().toString()
        return servicePeriod.days_of_week.includes(weekday)
    }
}

function weekBackFrom(from: Dayjs) {
    return Array.from({ length: daysInWeek }, (_, offset) => subtract(from, offset, 'day'))
}

function weekAheadFrom(from: Dayjs) {
    return Array.from({ length: daysInWeek }, (_, offset) => add(from, offset, 'day'))
}

/** For a service period, the last day before `date` it covers. */
function lastCoveredDayBefore(date: Dayjs) {
    const dayBefore = subtract(date, 1, 'day')

    return (servicePeriod: ServicePeriod) => {
        const { end } = dateRangeOf(servicePeriod)
        const endedEarlier = end && isBefore(end, dayBefore, 'day')
        const searchFrom = endedEarlier ? end : dayBefore

        return weekBackFrom(searchFrom).find(coveredBy(servicePeriod))
    }
}

/** For a service period, the first day after `date` it covers. */
function firstCoveredDayAfter(date: Dayjs) {
    const dayAfter = add(date, 1, 'day')

    return (servicePeriod: ServicePeriod) => {
        const { start } = dateRangeOf(servicePeriod)
        const startsLater = start && isAfter(start, dayAfter, 'day')
        const searchFrom = startsLater ? start : dayAfter

        return weekAheadFrom(searchFrom).find(coveredBy(servicePeriod))
    }
}

/** The latest of `days`, skipping missing ones. */
function latestOf(days: (Dayjs | undefined)[]) {
    const definedDays = days.filter(candidate => candidate !== undefined)
    return maxBy(definedDays, candidate => candidate.valueOf())
}

/** The earliest of `days`, skipping missing ones. */
function earliestOf(days: (Dayjs | undefined)[]) {
    const definedDays = days.filter(candidate => candidate !== undefined)
    return minBy(definedDays, candidate => candidate.valueOf())
}

function formatNotConfiguredRange(date: Dayjs, previousCoveredDay: Dayjs | undefined, nextCoveredDay: Dayjs | undefined) {
    if (previousCoveredDay && nextCoveredDay) return ` den ${format(date, DATE_TEMPLATES.longMonth)}`
    if (previousCoveredDay) return ` etter ${format(previousCoveredDay, DATE_TEMPLATES.longMonth)}`
    if (nextCoveredDay) return ` før ${format(nextCoveredDay, DATE_TEMPLATES.longMonth)}`
    return ''
}

export const notConfiguredLocationLabel = 'Mangler åpningstider'

/**
 * Text for a location-day no service period covers. The description places the day relative to the nearest
 * days that have opening hours, so it reads as "not set up yet" or "not set up any more" when the gap is open-ended.
 */
export function getNotConfiguredLocationText(location: Pick<Location, 'room_code' | 'service_periods'>, date: Dayjs): ClosedLocationText {
    const servicePeriods = location.service_periods ?? []
    const previousCoveredDay = latestOf(servicePeriods.map(lastCoveredDayBefore(date)))
    const nextCoveredDay = earliestOf(servicePeriods.map(firstCoveredDayAfter(date)))
    const range = formatNotConfiguredRange(date, previousCoveredDay, nextCoveredDay)

    return {
        label: notConfiguredLocationLabel,
        description: `Ingen åpningstider er satt opp for ${location.room_code}${range}`,
    }
}
